import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, ArrowRight, BookOpen, AlertTriangle } from 'lucide-react';
import { getBlogPosts } from '../services/apiService';
import { BlogPost } from '../types';

const IS_DEMO = process.env.REACT_APP_IS_DEMO === 'true';

const BlogContent = () => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getBlogPosts()
            .then(setPosts)
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-20">
                <Loader2 className="w-10 h-10 text-brand animate-spin" />
            </div>
        );
    }

    return (
        <div className="pt-36 pb-24 px-6 md:px-8 max-w-6xl mx-auto animate-fade-in">

            <Helmet>
                <title>Blog | Another Home</title>
                <meta name="description" content="Insights on property management, investment tips, and the luxury short-term rental market." />
                <link rel="canonical" href="https://www.anotherhome.us/blog" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.anotherhome.us/blog" />
                <meta property="og:title" content="Blog | Another Home" />
                <meta property="og:description" content="Insights on property management, investment tips, and the luxury short-term rental market." />
                <meta property="og:image" content="https://www.anotherhome.us/img/og-image.jpg" />
            </Helmet>

            {/* DEMO BANNER */}
            {IS_DEMO && (
                <div className="mb-10 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl px-6 py-4 text-sm font-medium">
                    <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-500" />
                    <span>
                        <strong>Preview Mode</strong> — This blog section is currently showing sample articles.
                        Real content will be published at launch.
                    </span>
                </div>
            )}

            {/* HEADER */}
            <div className="mb-14 text-center">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <span className="w-10 h-[2px] bg-brand"></span>
                    <BookOpen className="w-6 h-6 text-brand" />
                    <span className="w-10 h-[2px] bg-brand"></span>
                </div>
                <h1 className="text-5xl font-serif font-bold text-primary mb-4">Our Blog</h1>
                <p className="text-gray-500 max-w-xl mx-auto text-lg leading-relaxed">
                    Insights on property management, investment tips, and the luxury short-term rental market.
                </p>
            </div>

            {/* POSTS GRID */}
            {posts.length === 0 ? (
                <div className="text-center py-20 text-gray-400">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">No articles published yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {posts.map(post => (
                        <article
                            key={post.id}
                            onClick={() => navigate(`/blog/${post.slug}`)}
                            className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
                        >
                            {/* Cover Image */}
                            <div className="relative overflow-hidden h-52">
                                {post.coverImageUrl ? (
                                    <img
                                        src={post.coverImageUrl}
                                        alt={post.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                        <BookOpen className="w-10 h-10 text-gray-300" />
                                    </div>
                                )}
                                {post.category && (
                                    <span className="absolute top-4 left-4 bg-brand text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow">
                                        {post.category}
                                    </span>
                                )}
                            </div>

                            {/* Body */}
                            <div className="p-6">
                                <p className="text-xs text-gray-400 font-medium mb-2 uppercase tracking-wider">
                                    {post.author} &mdash;{' '}
                                    {post.createdAt ? new Date(post.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                                </p>
                                <h2 className="text-xl font-serif font-bold text-primary mb-3 leading-snug group-hover:text-brand transition-colors">
                                    {post.title}
                                </h2>
                                <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">
                                    {post.excerpt}
                                </p>
                                <span className="inline-flex items-center gap-1.5 text-brand font-bold text-sm group-hover:gap-3 transition-all">
                                    Read article <ArrowRight className="w-4 h-4" />
                                </span>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BlogContent;
